export const SUPPORTED_LOCALES = ['ko', 'en', 'ja'] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]

export const LANGUAGE_CODES = ['system', ...SUPPORTED_LOCALES] as const
export type LanguageCode = (typeof LANGUAGE_CODES)[number]

export const DEFAULT_LANGUAGE_CODE = 'system' satisfies LanguageCode
export const FALLBACK_LOCALE = 'en' satisfies Locale

export const LANGUAGE_CODE_COOKIE_KEY = 'PODO_LANGUAGE_CODE'
export const SYSTEM_LOCALE_COOKIE_KEY = 'PODO_SYSTEM_LOCALE'
export const EFFECTIVE_LOCALE_COOKIE_KEY = 'PODO_EFFECTIVE_LOCALE'
export const SYSTEM_LOCALE_QUERY_PARAM = 'podo_system_locale'

export const LOCALE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365

/**
 * 학습 언어(langType) 축. 티켓·코스·수업의 `LANG_TYPE` 과 같은 표기다.
 * 표시 언어(Locale)와 축이 다르므로 한국어가 KR/ko 로 갈린다.
 */
export const LEARNING_LANG_TYPES = ['EN', 'JP', 'KR'] as const
export type LearningLangType = (typeof LEARNING_LANG_TYPES)[number]

/**
 * 학습 언어 → 표시 언어 매핑.
 *
 * 주의: 이 값은 onetalk `externalMessageId`(중복 발송을 막는 멱등키)에도 들어간다.
 * 표기를 바꾸면 이미 보낸 메시지가 다른 메시지로 인식돼 재발송되므로, 뜻이 통하는
 * 다른 표기로 갈아끼우지 마라.
 */
export const LANG_TYPE_TO_LOCALE = {
  EN: 'en',
  JP: 'ja',
  KR: 'ko',
} as const satisfies Record<LearningLangType, Locale>

export const podoLangTypeToLocale = (langType: LearningLangType): Locale => LANG_TYPE_TO_LOCALE[langType]

/**
 * 로그인 국가 선택(`PODO_LOGIN_COUNTRY`) → 표시 언어 매핑.
 *
 * 국가 선택은 "어디서 서비스를 쓰는가"이지 "무슨 언어로 볼까"가 아니다. 그래서 표시 언어를
 * 직접 고른 사용자(`PODO_LANGUAGE_CODE`)와 기기 언어 다음에 오는, 가장 약한 신호로 다룬다 —
 * 기기 언어가 지원 목록 밖일 때 en 대신 쓰는 fallback 이다.
 * 이 값을 `LANGUAGE_CODE_COOKIE_KEY` 에 써넣지 마라 — 고르지도 않은 언어가 명시적 선택으로
 * 승격돼 1년간 고착되고, 그 뒤로는 기기 언어를 영영 보지 않게 된다.
 */
export const LOGIN_COUNTRY_TO_LOCALE = {
  KR: 'ko',
  JP: 'ja',
} as const satisfies Record<string, Locale>

export const localeFromLoginCountry = (value: unknown): Locale | null => {
  if (typeof value !== 'string') {
    return null
  }

  return LOGIN_COUNTRY_TO_LOCALE[value.trim().toUpperCase() as keyof typeof LOGIN_COUNTRY_TO_LOCALE] ?? null
}

export const isSupportedLocale = (value: unknown): value is Locale => {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

export const isLanguageCode = (value: unknown): value is LanguageCode => {
  return typeof value === 'string' && (LANGUAGE_CODES as readonly string[]).includes(value)
}
